/**
 * Mobile Animated Modal Component
 * Reusable animated modal that feels like a native mobile app
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ysr2Mobile } from '../styles/mobileDesignSystem';

const MobileAnimatedModal = ({ 
  isOpen, 
  onClose, 
  children, 
  title, 
  icon: Icon,
  /** 'bottom' | 'center' | 'center-high' (centered, shifted up for tab bar) | 'top' */
  position = 'bottom',
  /** When `position` is `bottom`, distance from viewport bottom (lifts sheet higher). Default clears tab bar. */
  bottomInset = '112px',
  maxWidth = 'sm',
  showBackdrop = true,
  className = '',
  overlayZIndex = 100000,
}) => {
  if (!isOpen) return null;

  const overlayFlexStyle = (() => {
    const base = { zIndex: overlayZIndex };
    if (position === 'bottom') {
      return { ...base, alignItems: 'flex-end', paddingBottom: bottomInset, paddingTop: 0 };
    }
    if (position === 'top') {
      return { ...base, alignItems: 'flex-start', paddingTop: '20px', paddingBottom: '20px' };
    }
    if (position === 'center-high') {
      return {
        ...base,
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'max(28px, env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(112px + env(safe-area-inset-bottom, 0px))',
      };
    }
    return { ...base, alignItems: 'center', paddingBottom: '20px', paddingTop: '0px' };
  })();

  const getMaxWidthClasses = () => {
    switch (maxWidth) {
      case 'xs':
        return 'max-w-xs';
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      default:
        return 'max-w-sm';
    }
  };

  return createPortal((
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 flex justify-center"
        style={overlayFlexStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        {/* Backdrop */}
        {showBackdrop && (
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            transition={{ duration: 0.25, ease: "easeOut" }}
          />
        )}
        
        {/* Modal Content */}
        <motion.div
          className={`relative w-full mx-4 ${getMaxWidthClasses()} max-h-[calc(100dvh-140px)] overflow-y-auto overscroll-contain ${className}`}
          initial={{ 
            y: position === 'bottom' ? 30 : position === 'top' ? -30 : position === 'center-high' ? 12 : 0,
            opacity: 0
          }}
          animate={{ 
            y: 0, 
            opacity: 1
          }}
          exit={{ 
            y: position === 'bottom' ? 30 : position === 'top' ? -30 : position === 'center-high' ? 12 : 0,
            opacity: 0
          }}
          transition={{ 
            duration: 0.3,
            ease: "easeOut"
          }}
        >
          <div className={`${ysr2Mobile.surfaceStrong} p-6`}>
            {/* Header */}
            {title && (
              <div className="flex items-center space-x-3 mb-4">
                {Icon && <Icon className="w-6 h-6 text-blue-200" />}
                <h3 className="text-lg font-semibold text-white">{title}</h3>
              </div>
            )}
            
            {/* Content */}
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  ), document.body);
};

export default MobileAnimatedModal;
